import { useEffect } from "react";
import { useMetaContext } from "./MetaContext";

interface MetaTagsProps {
  title: string;
  description: string;
  ogImage?: string;
  url?: string;
  type?: string;
  articlePublishedTime?: string;
  robots?: string;
}

function setMetaTag(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const DEFAULT_TITLE = "OmniCanvas - The Infinite Spatial Second Brain";
const DEFAULT_DESCRIPTION = "Capture everything. Link anything. Visualize your thoughts on a canvas that never ends.";
const DEFAULT_IMAGE = "https://pub-530c8de906034aa3846ea946c751a5aa.r2.dev/og-image.png";
const DEFAULT_URL = "https://omnicanvasnotes.com";

export function useMetaTags({
  title,
  description,
  ogImage = DEFAULT_IMAGE,
  url = DEFAULT_URL,
  type = "website",
  articlePublishedTime,
  robots,
}: MetaTagsProps) {
  // During SSR, push meta data into the context so the prerender script
  // can inject it into the HTML <head> (useEffect never fires on the server).
  const metaCtx = useMetaContext();
  if (metaCtx) {
    metaCtx.set({ title, description, ogImage, url, type, articlePublishedTime, robots });
  }

  useEffect(() => {
    document.title = title;

    setMetaTag("name", "description", description);
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:url", url);
    setMetaTag("property", "og:type", type);
    setMetaTag("property", "og:site_name", "OmniCanvas");
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", ogImage);

    if (articlePublishedTime) {
      setMetaTag("property", "article:published_time", articlePublishedTime);
    }

    if (robots) {
      setMetaTag("name", "robots", robots);
    }

    setCanonical(url);

    return () => {
      document.title = DEFAULT_TITLE;
      setMetaTag("name", "description", DEFAULT_DESCRIPTION);
      setMetaTag("property", "og:title", DEFAULT_TITLE);
      setMetaTag("property", "og:description", DEFAULT_DESCRIPTION);
      setMetaTag("property", "og:image", DEFAULT_IMAGE);
      setMetaTag("property", "og:url", DEFAULT_URL);
      setMetaTag("property", "og:type", "website");
      setMetaTag("name", "twitter:card", "summary_large_image");
      setMetaTag("name", "twitter:title", DEFAULT_TITLE);
      setMetaTag("name", "twitter:description", DEFAULT_DESCRIPTION);
      setMetaTag("name", "twitter:image", DEFAULT_IMAGE);
      setCanonical(DEFAULT_URL);

      const articleMeta = document.querySelector('meta[property="article:published_time"]');
      if (articleMeta) articleMeta.remove();
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    };
  }, [title, description, ogImage, url, type, articlePublishedTime]);
}
